'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import type { Route } from 'next';
import { useCookieConsent } from '@/lib/hooks/useCookieConsent';
import type { Locale } from '@/lib/i18n/locales';
import styles from './CookieConsentBanner.module.scss';

interface CookieConsentBannerProps {
  copy: {
    title: string;
    description: string;
    accept: string;
    reject: string;
    learnMore: string;
  };
  locale: Locale;
}

export function CookieConsentBanner({
  copy,
  locale,
}: CookieConsentBannerProps) {
  const { consentStatus, acceptCookies, rejectCookies } = useCookieConsent();
  const pathname = usePathname();

  // Check if we're on the cookies page
  const isOnCookiesPage = pathname?.includes('/cookies');

  // Disable scroll when banner is active
  useEffect(() => {
    if (consentStatus === 'pending' && !isOnCookiesPage) {
      // Save original overflow value
      const originalOverflow = document.body.style.overflow;

      // Disable scroll
      document.body.style.overflow = 'hidden';

      // Restore scroll on cleanup
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [consentStatus, isOnCookiesPage]);

  // Don't show banner on cookies page or if consent is not pending
  if (consentStatus !== 'pending' || isOnCookiesPage) {
    return null;
  }

  return (
    <div className={styles.overlay}>
      <div className={styles.banner}>
        <div className={styles.content}>
          <h2 className={styles.title}>{copy.title}</h2>
          <p className={styles.description}>
            {copy.description}{' '}
            <Link href={`/${locale}/cookies` as Route} className={styles.link}>
              {copy.learnMore}
            </Link>
          </p>
        </div>
        <div className={styles.actions}>
          <button
            type="button"
            className={`${styles.button} ${styles.buttonReject}`}
            onClick={rejectCookies}
          >
            {copy.reject}
          </button>
          <button
            type="button"
            className={`${styles.button} ${styles.buttonAccept}`}
            onClick={acceptCookies}
          >
            {copy.accept}
          </button>
        </div>
      </div>
    </div>
  );
}
